// The dashboard designer's canvas: the dashboard as people will see it — its
// name in the dashboard's colour, the run-time filter card, and the tiles on
// the 12-column grid — with the editing affordances on top:
//   • drag a tile to move it (Ctrl/⌘/Shift-click selects several, which move
//     together); tiles below make room, tiles above never move;
//   • a selected tile has handles on all four edges and corners;
//   • a full-width empty band left by a move offers "Remove empty space";
//   • on the Global criteria tab the tiles a criterion reaches are outlined,
//     on the Drill-through tab the source tiles are tagged and a click on a
//     tile makes it the open link's source.
// Geometry lives in layoutOps.ts; this file only turns pointer gestures into
// those calls.
//
// Tiles show the dashboard's REAL data: the whole (unsaved) model is run
// through the same /visual-data/dashboard call Preview uses, so each tile gets
// its bound global criteria — a visual run on its own (the workbench
// thumbnail) has no dates and often returns nothing. The run is debounced and
// repeats only when something that changes the data changes (tile content,
// bindings, criteria, the filter card's applied values) — never on a move or
// resize. The filter card's Apply re-runs the tiles with its values (a
// preview: they are never saved).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { PointerEvent as ReactPointerEvent } from 'react'
import toast from 'react-hot-toast'
import { ArrowUp, EyeOff, Filter as FilterIcon, FileText, LayoutGrid, Plus, Play, Loader2 } from 'lucide-react'
import {
  useDashboardAccent, DashboardFilterCard, dashApplyButtonStyle, InsightVisual, readDataError,
} from '@twasta/visual-engine'
import {
  CriteriaBar, formatCriteriaValueForInput, type VisualCriteriaPromptVar,
} from '@twasta/page-runtime/txn'
import {
  makeVisualCriteriaRuntime, fetchDashboardData, type DashboardDataResult, type DashboardTile,
} from '@/api/visualData'
import type {
  DashboardBody, DashboardDrillThrough, DashboardGlobalCriterion, DashboardVisual,
  DashboardModel,
} from './types'
import { extractVisualParams } from './visualParams'
import {
  COLS, emptyBands, moveTiles, resizeTile, snap, bottomRow, type ResizeMode,
} from './layoutOps'
import { tileContentKey, visualRefToObjName, type PanelTab } from './designerShared'

const RUN_DEBOUNCE_MS = 700

function pruneEmpty(v: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [k, x] of Object.entries(v)) if (x != null && String(x).trim() !== '') out[k] = x
  return out
}

const ROW = 60 // px per grid row
const RESIZE_MODES: ResizeMode[] = ['n', 's', 'w', 'e', 'nw', 'ne', 'sw', 'se']
const RESIZE_LABEL: Record<ResizeMode, string> = {
  n: 'top edge', s: 'bottom edge', w: 'left edge', e: 'right edge',
  nw: 'top-left corner', ne: 'top-right corner', sw: 'bottom-left corner', se: 'bottom-right corner',
}

/** Does global criterion `g` reach tile `t` at run time? A tile with bindings
 *  receives only what its bindings say; one without follows BROADCAST_TO. */
export function criterionReaches(g: DashboardGlobalCriterion, t: DashboardVisual): boolean {
  const binds = t.CRITERIA_BINDINGS || []
  if (binds.length) return binds.some((b) => b.BIND_TYPE === 'global' && b.GLOBAL_PARAM === g.PARAM_NAME)
  return g.BROADCAST_TO.includes(t.VISUAL_ID)
}

export interface DesignCanvasProps {
  project: string
  objectName: string
  model: DashboardModel
  canEdit: boolean
  tab: PanelTab
  selectedIds: ReadonlySet<string>
  visualContents: Record<string, unknown | null>
  appColor: string | null
  /** Find bar: matching tile ids (empty when no search) and the current one. */
  findQuery: string
  findMatches: readonly string[]
  findCurrent: string | null
  /** The criterion / link card last touched on the panel. */
  focusedCriterion: DashboardGlobalCriterion | null
  focusedDrill: DashboardDrillThrough | null
  onSelect: (id: string | null, mode?: 'single' | 'toggle' | 'keep') => void
  onTilesChange: (tiles: DashboardVisual[]) => void
  onRemoveBand: (from: number, to: number) => void
  onOpenAdd: () => void
  /** A criterion chip on the filter card was clicked. */
  onPickCriterion: (paramName: string) => void
  registerTileRef: (id: string, el: HTMLDivElement | null) => void
  toast?: { message: string; onUndo: () => void; onDismiss: () => void } | null
  /** Bumped when a tile's visual was edited elsewhere — re-runs the tiles. */
  dataVersion?: number
}

export default function DesignCanvas(props: DesignCanvasProps) {
  const {
    project, objectName, model, canEdit, tab, selectedIds, visualContents, appColor,
    findQuery, findMatches, findCurrent, focusedCriterion, focusedDrill,
    onSelect, onTilesChange, onRemoveBand, onOpenAdd, onPickCriterion, registerTileRef,
  } = props
  const body = model.DASHBOARD
  const tiles = body.VISUALS
  const gridRef = useRef<HTMLDivElement>(null)
  const [dragging, setDragging] = useState<{ ids: string[] } | null>(null)
  const { ref: accentRef, style: accentStyle } = useDashboardAccent(body.THEME_COLOR, appColor)

  // ── Pointer gestures ─────────────────────────────────────────────
  const startDrag = useCallback((e: ReactPointerEvent, id: string, mode: 'move' | ResizeMode) => {
    if (e.button !== 0) return
    e.stopPropagation()
    const additive = e.ctrlKey || e.metaKey || e.shiftKey
    if (mode === 'move' && additive) {
      e.preventDefault()
      onSelect(id, 'toggle')
      return
    }
    // A press on an already-selected tile keeps a multi-selection (group drag).
    const keep = mode === 'move' && selectedIds.has(id)
    onSelect(id, keep ? 'keep' : 'single')
    if (!canEdit) return
    e.preventDefault()
    const rect = gridRef.current?.getBoundingClientRect()
    const colW = rect && rect.width ? rect.width / COLS : 80
    const sx = e.clientX
    const sy = e.clientY
    const orig = tiles
    const ids = mode === 'move' ? (keep ? [...selectedIds] : [id]) : [id]
    let moved = false
    let last = ''
    const onMove = (ev: PointerEvent) => {
      if (!moved && Math.abs(ev.clientX - sx) < 4 && Math.abs(ev.clientY - sy) < 4) return
      if (!moved) { moved = true; setDragging({ ids }) }
      const dx = snap((ev.clientX - sx) / colW)
      const dy = snap((ev.clientY - sy) / ROW)
      const key = `${dx}:${dy}`
      if (key === last) return
      last = key
      try {
        onTilesChange(mode === 'move' ? moveTiles(orig, ids, dx, dy) : resizeTile(orig, id, mode, dx, dy))
      } catch (err) {
        console.error('Tile drag failed', err)
      }
    }
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
      document.body.style.userSelect = ''
      setDragging(null)
    }
    document.body.style.userSelect = 'none'
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
  }, [canEdit, onSelect, onTilesChange, selectedIds, tiles])

  // ── Filter card (the run-time CriteriaBar, card look) ─────────────
  const promptVars = useMemo<VisualCriteriaPromptVar[]>(
    () => body.GLOBAL_CRITERIA
      .filter((c) => (c.PARAM_NAME || '').trim() !== '')
      .map((c) => ({
        name: c.PARAM_NAME,
        label: c.PROMPT_LABEL || c.PARAM_NAME,
        data_type: c.DATA_TYPE,
        operator: c.OPERATOR,
        default: c.DEFAULT_VALUE,
        has_default: c.DEFAULT_VALUE != null && String(c.DEFAULT_VALUE).trim() !== '',
        required: c.mandatory === true || String(c.mandatory ?? '').toLowerCase() === 'true',
        options: c.options,
        lookup: c.lookup,
        lookup_filter: c.lookup_filter,
        lookup_filter_rules: c.lookup_filter_rules,
        lookup_sql: c.lookup_sql,
        lookup_title: c.lookup_title,
        lookup_sql_data_col: c.lookup_sql_data_col,
        has_item_change: !!c.item_change,
      })),
    [body.GLOBAL_CRITERIA],
  )
  const [critCollapsed, setCritCollapsed] = useState(true)
  const applyCriteria = () => {
    setApplied(pruneEmpty(values))
    setCritCollapsed(true)
  }
  const criteriaRuntime = useMemo(
    () => makeVisualCriteriaRuntime({
      project, kind: 'dashboard', obj_name: objectName, inline_model: model,
      notify: { success: toast.success, error: toast.error, info: (m) => toast(m) },
    }),
    [project, objectName, model],
  )

  // ── Tile data (the dashboard run, like Preview) ───────────────────
  const [typed, setTyped] = useState<Record<string, string>>({})
  const [applied, setApplied] = useState<Record<string, string>>({})
  const [run, setRun] = useState<{ data: DashboardDataResult | null; loading: boolean; error: string | null }>(
    { data: null, loading: false, error: null },
  )
  const modelRef = useRef(model)
  modelRef.current = model
  const runSeq = useRef(0)
  // What the data depends on — positions, sizes, titles and the title bar
  // do not change any tile's data, so moving tiles never re-queries.
  const dataKey = useMemo(() => JSON.stringify({
    v: body.VISUALS.map((t) => ({
      id: t.VISUAL_ID, ref: t.VISUAL_CONFIG_REF, kind: t.TILE_KIND, rep: t.REPORT_OBJ, b: t.CRITERIA_BINDINGS,
    })),
    g: body.GLOBAL_CRITERIA,
    a: applied,
    n: props.dataVersion ?? 0,
  }), [body.VISUALS, body.GLOBAL_CRITERIA, applied, props.dataVersion])
  useEffect(() => {
    if (!project || !objectName || body.VISUALS.length === 0) return undefined
    const seq = ++runSeq.current
    setRun((r) => ({ ...r, loading: true }))
    const t = window.setTimeout(() => {
      const sent = pruneEmpty(applied)
      fetchDashboardData({
        project,
        obj_name: objectName,
        inline_model: modelRef.current,
        global_criteria: Object.keys(sent).length ? sent : undefined,
      })
        .then((data) => { if (seq === runSeq.current) setRun({ data, loading: false, error: null }) })
        .catch((err) => {
          console.error('Dashboard canvas: tile data failed', err)
          if (seq === runSeq.current) setRun((r) => ({ ...r, loading: false, error: readDataError(err).message }))
        })
    }, RUN_DEBOUNCE_MS)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, objectName, dataKey])
  const tileData = useMemo(() => {
    const m = new Map<string, DashboardTile>()
    for (const t of run.data?.tiles || []) m.set(t.VISUAL_ID, t)
    return m
  }, [run.data])
  const resolvedGlobals = run.data?.dashboard?.resolvedGlobals || {}
  // What the card shows: values typed here (preview only, never saved) over
  // each criterion's resolved default as the input would hold it.
  const values = useMemo(() => {
    const v: Record<string, string> = {}
    for (const c of body.GLOBAL_CRITERIA) {
      if (!c.PARAM_NAME) continue
      if (typed[c.PARAM_NAME] !== undefined) { v[c.PARAM_NAME] = typed[c.PARAM_NAME]; continue }
      // The server-RESOLVED default (a :calendar token → a date), as Preview.
      const resolved = resolvedGlobals[c.PARAM_NAME]
      const raw = resolved != null && resolved !== '' ? resolved : c.DEFAULT_VALUE
      const seed = formatCriteriaValueForInput(raw, c.DATA_TYPE)
      if (seed !== '') v[c.PARAM_NAME] = seed
    }
    return v
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [body.GLOBAL_CRITERIA, typed, run.data])

  // ── Derived tile state ───────────────────────────────────────────
  const drillsBySource = useMemo(() => {
    const m = new Map<string, DashboardDrillThrough[]>()
    for (const d of body.DRILL_THROUGH) {
      const list = m.get(d.SOURCE_VISUAL_ID) || []
      list.push(d)
      m.set(d.SOURCE_VISUAL_ID, list)
    }
    return m
  }, [body.DRILL_THROUGH])
  const validGlobals = useMemo(
    () => new Set(body.GLOBAL_CRITERIA.map((c) => c.PARAM_NAME).filter(Boolean)),
    [body.GLOBAL_CRITERIA],
  )
  const searching = findQuery.trim() !== ''
  const bottom = bottomRow(tiles)
  const gridH = Math.max(bottom, 4) * ROW + (dragging ? 120 : 12)
  const bands = dragging ? [] : emptyBands(tiles)
  const status = [
    `${tiles.length} tile${tiles.length === 1 ? '' : 's'}`,
    `${body.GLOBAL_CRITERIA.length} global criteri${body.GLOBAL_CRITERIA.length === 1 ? 'on' : 'a'}`,
    `${body.DRILL_THROUGH.length} drill-through${body.DRILL_THROUGH.length === 1 ? '' : 's'}`,
  ].join(' · ')

  return (
    <main className="dd-canvas" aria-label="Dashboard layout">
      {props.toast && (
        <div className="dd-toast" role="status">
          <span style={{ flex: '1 1 260px' }}>{props.toast.message}</span>
          <button onClick={props.toast.onUndo}>Undo</button>
          <button className="x" onClick={props.toast.onDismiss} aria-label="Dismiss">×</button>
        </div>
      )}
      <section
        ref={accentRef}
        className="vdo-float dd-page"
        style={accentStyle}
        onPointerDown={(e) => { if (e.target === e.currentTarget) onSelect(null) }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 280px', minWidth: 0 }}>
            <h1 style={{
              margin: 0, fontSize: 22, fontWeight: 700, letterSpacing: '-.01em',
              color: 'var(--dash-ink, var(--color-accent))',
            }}>
              {body.DASHBOARD_NAME || objectName}
            </h1>
            {body.DASHBOARD_DESCR && (
              <div style={{ fontSize: 14, color: 'var(--color-text-faint)', marginTop: 3 }}>{body.DASHBOARD_DESCR}</div>
            )}
          </div>
          <div style={{ fontSize: 13, color: 'var(--color-text-faint)', paddingTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
            {run.loading && tiles.length > 0 && (
              <><Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> Loading data ·</>
            )}
            {status}
          </div>
        </div>

        {tiles.length === 0 ? (
          <div className="dd-empty">
            <span className="ic"><LayoutGrid size={28} /></span>
            <h2 style={{ margin: '4px 0 0', fontSize: 21, fontWeight: 700 }}>No visuals yet</h2>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--color-text-faint)', maxWidth: 460, lineHeight: 1.5 }}>
              Add a chart, table or report from this project. Each one becomes a tile you can move and resize.
            </p>
            {canEdit && (
              <button className="dd-addbtn" style={{ marginTop: 6 }} onClick={onOpenAdd}>
                <Plus size={17} /> Add visual
              </button>
            )}
          </div>
        ) : (
          <>
            {promptVars.length > 0 && (
              <DashboardFilterCard>
                <CriteriaBar
                  look="card"
                  project={project}
                  promptVars={promptVars}
                  values={values}
                  onChange={(name, val) => setTyped((t) => ({ ...t, [name]: val }))}
                  onSubmit={applyCriteria}
                  runtime={criteriaRuntime}
                  collapsed={critCollapsed}
                  onToggleCollapsed={() => {
                    setCritCollapsed((c) => !c)
                    if (critCollapsed && promptVars[0]) onPickCriterion(promptVars[0].name)
                  }}
                  actions={(
                    <button
                      type="button"
                      onClick={applyCriteria}
                      disabled={run.loading}
                      title="Runs the tiles below with these values. A preview only: defaults are set on Global criteria."
                      style={dashApplyButtonStyle(run.loading)}
                    >
                      <Play size={14} /> {run.loading ? 'Running…' : 'Apply'}
                    </button>
                  )}
                />
              </DashboardFilterCard>
            )}

            {run.error && (
              <div role="alert" style={{
                fontSize: 13.5, color: 'var(--color-error)', background: 'var(--color-error-weak)',
                borderRadius: 10, padding: '8px 12px',
              }}>
                The tiles&apos; data could not be loaded: {run.error}
              </div>
            )}
            {run.data?.needsInput && (
              <div role="status" style={{
                fontSize: 13.5, color: 'var(--color-text-secondary)', background: 'var(--color-surface-2)',
                borderRadius: 10, padding: '8px 12px',
              }}>
                A required filter has no value, so no tile ran. Fill it in on the filter card and press Apply.
              </div>
            )}
            <div className="dd-gridwrap">
              <div
                ref={gridRef}
                className={`dd-grid${dragging ? ' drag' : ''}`}
                style={{ height: gridH }}
                onPointerDown={(e) => { if (e.target === e.currentTarget) onSelect(null) }}
              >
                {bands.map((g) => {
                  const d = g.to - g.from
                  const hpx = d * ROW - 12
                  return (
                    <div
                      key={`${g.from}-${g.to}`}
                      className={`dd-gap${hpx < 40 ? ' thin' : ''}`}
                      style={{ top: g.from * ROW + 6 + Math.min(0, hpx) / 2, height: Math.max(hpx, 2) }}
                    >
                      {canEdit && (
                        <button
                          className="dd-gapbtn"
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={() => onRemoveBand(g.from, g.to)}
                        >
                          <ArrowUp size={16} /> Remove empty space
                          <span className="sz2">· {d === 1 ? '1 row' : `${d} rows`}</span>
                        </button>
                      )}
                    </div>
                  )
                })}
                {tiles.map((t) => {
                  const selected = selectedIds.has(t.VISUAL_ID)
                  const isMoving = !!dragging && dragging.ids.includes(t.VISUAL_ID)
                  let cls = 'dd-tile'
                  let tag: { text: string; kind: 'f' | 'c' } | null = null
                  if (selected && tab === 'layout') cls += ' sel'
                  if (isMoving) cls += ' moving'
                  if (searching) {
                    if (!findMatches.includes(t.VISUAL_ID)) cls += ' dim'
                    else if (findCurrent === t.VISUAL_ID) cls += ' found'
                  } else if (tab === 'criteria' && focusedCriterion) {
                    if (criterionReaches(focusedCriterion, t)) {
                      cls += ' conn'
                      tag = { text: `← ${focusedCriterion.PROMPT_LABEL || focusedCriterion.PARAM_NAME}`, kind: 'f' }
                    } else cls += ' dim'
                  } else if (tab === 'drill') {
                    const n = drillsBySource.get(t.VISUAL_ID)?.length || 0
                    if (n > 0) {
                      tag = { text: n > 1 ? `${n} drills ›` : 'drill ›', kind: 'c' }
                      if (focusedDrill && focusedDrill.SOURCE_VISUAL_ID === t.VISUAL_ID) cls += ' src'
                    }
                  }
                  return (
                    <div
                      key={t.VISUAL_ID}
                      ref={(el) => registerTileRef(t.VISUAL_ID, el)}
                      className={`dd-cell${isMoving ? ' moving' : ''}`}
                      style={{
                        left: `${(t.POSITION.X / COLS) * 100}%`,
                        width: `${(t.POSITION.WIDTH / COLS) * 100}%`,
                        top: t.POSITION.Y * ROW,
                        height: t.POSITION.HEIGHT * ROW,
                      }}
                      tabIndex={0}
                      role="button"
                      aria-pressed={selected}
                      aria-label={`${t.VISUAL_NAME || t.VISUAL_ID}, ${t.POSITION.WIDTH} by ${t.POSITION.HEIGHT}`}
                      onPointerDown={(e) => startDrag(e, t.VISUAL_ID, 'move')}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(t.VISUAL_ID) } }}
                    >
                      <div className={cls}>
                        <TileFace
                          tile={t}
                          data={tileData.get(t.VISUAL_ID)}
                          loading={run.loading && !tileData.has(t.VISUAL_ID)}
                          tag={tag}
                          content={visualContents[tileContentKey(t)]}
                          validGlobals={validGlobals}
                        />
                        {canEdit && selected && selectedIds.size === 1 && tab === 'layout' && RESIZE_MODES.map((m) => (
                          <button
                            key={m}
                            type="button"
                            tabIndex={-1}
                            className={`dd-hdl ${m}`}
                            aria-label={`Drag the ${RESIZE_LABEL[m]}`}
                            onPointerDown={(e) => startDrag(e, t.VISUAL_ID, m)}
                          />
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
            {canEdit && (
              <button className="dd-addrow" onClick={onOpenAdd}><Plus size={16} /> Add visual</button>
            )}
          </>
        )}
      </section>
    </main>
  )
}

// One tile's face: title bar (as the designer sees it — struck through when it
// will not show at run time), the live visual, and the bindings summary.
function TileFace({ tile, data, loading, tag, content, validGlobals }: {
  tile: DashboardVisual
  /** This tile's dataset from the dashboard run (undefined until it lands). */
  data: DashboardTile | undefined
  loading: boolean
  tag: { text: string; kind: 'f' | 'c' } | null
  content: unknown | null | undefined
  validGlobals: ReadonlySet<string>
}) {
  const headerShown = tile.SHOW_HEADER !== false
  const isReport = tile.TILE_KIND === 'report'
  const visualObj = isReport ? '' : visualRefToObjName(tile.VISUAL_CONFIG_REF || '')
  const params = useMemo(() => (content ? extractVisualParams(content) : []), [content])
  let wired = 0
  let broken = 0
  const tips: string[] = []
  for (const p of params) {
    const b = (tile.CRITERIA_BINDINGS || []).find((x) => x.VISUAL_PARAM === p.name)
    if (b?.BIND_TYPE === 'global') {
      wired++
      if (b.GLOBAL_PARAM && !validGlobals.has(b.GLOBAL_PARAM)) broken++
      tips.push(`${p.name} ← ${b.GLOBAL_PARAM || '?'}`)
    } else if (b?.BIND_TYPE === 'constant') {
      wired++
      tips.push(`${p.name} = ${b.CONSTANT_VALUE ?? ''}`)
    } else tips.push(`${p.name} (default)`)
  }
  const msg = (text: string, tone?: 'error') => (
    <div className="dd-placeholder" style={{ borderStyle: 'solid', borderColor: 'transparent', color: tone === 'error' ? 'var(--color-error)' : undefined }}>
      {text}
    </div>
  )
  return (
    <>
      <div className="dd-th">
        {!headerShown && (
          <EyeOff size={14} aria-label="Title bar hidden at run time" style={{ flexShrink: 0, color: 'var(--color-text-faint)' }} />
        )}
        <b className={headerShown ? '' : 'off'} title={tile.VISUAL_ID}>{tile.VISUAL_NAME || tile.VISUAL_ID}</b>
        {tag && <span className={`dd-tag ${tag.kind}`}>{tag.text}</span>}
        <span className="sz">{tile.POSITION.WIDTH}×{tile.POSITION.HEIGHT}</span>
      </div>
      <div className="dd-tbody">
        {isReport ? (
          <div className="dd-placeholder" style={{ borderStyle: 'solid', borderColor: 'transparent' }}>
            <FileText size={28} style={{ color: 'var(--color-accent)' }} />
            <span style={{ fontWeight: 600, color: 'var(--color-text-secondary)' }}>
              {tile.REPORT_OBJ ? `Report · ${tile.REPORT_OBJ}` : '(no report picked)'}
            </span>
          </div>
        ) : !visualObj ? (
          <div className="dd-placeholder">(no visual picked)</div>
        ) : data?.error ? (
          msg(data.error, 'error')
        ) : data?.needsInput ? (
          msg('Needs a filter value')
        ) : data?.jsonData && data.visualLayout ? (
          data.jsonData.length > 1 ? (
            <InsightVisual
              jsonData={data.jsonData}
              visualLayout={data.visualLayout}
              optionsArr={data.optionsArr}
              style={{ position: 'absolute', inset: 0, height: '100%', width: '100%' }}
            />
          ) : msg('No data for these filters')
        ) : loading || !data ? (
          <div className="dd-placeholder" style={{ borderStyle: 'solid', borderColor: 'transparent' }}>
            <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
          </div>
        ) : (
          msg('No data')
        )}
      </div>
      {params.length > 0 && (
        <div className="dd-tf" title={tips.join('\n')}>
          <FilterIcon size={12} style={{ flexShrink: 0 }} />
          <span>{wired}/{params.length} filter{params.length === 1 ? '' : 's'} wired</span>
          {broken > 0 && <span className="bad">· {broken} broken</span>}
        </div>
      )}
    </>
  )
}
